/**
 * TODO: Remove legacy program access once sites/me exposes all *_enabled preferences.
 * Legacy site program detection uses licenses, platform capabilities, and nested
 * sitePreferences / programs buckets — not site-level *_enabled flags.
 */
import { extractProgramCodesFromCapabilities } from 'store/siteManager/helpers'

const DWB_SITE_PREFERENCE_NAME = 'dwbp_enabled'
const STANDALONE_DWB_BLOCKED_PROGRAMS = ['K5P', 'MSP', 'HSP']

const HS_LICENSE_PROGRAM_CODES = ['HSP', 'K12P', 'HSPF']
const K8_LICENSE_PROGRAM_CODES = ['K5P', 'K8P', 'MSP']

const isTruthyBooleanPreference = value => {
  if (typeof value === 'boolean') return value
  if (value === null || value === undefined) return false
  return value === 'true' || value === true || value === 1
}

const readDwbEnabledFromPreferenceBucket = bucket => {
  if (!bucket || typeof bucket !== 'object') return null
  if (bucket[DWB_SITE_PREFERENCE_NAME] !== undefined) {
    return isTruthyBooleanPreference(bucket[DWB_SITE_PREFERENCE_NAME])
  }
  if (bucket.dwbpEnabled !== undefined) {
    return isTruthyBooleanPreference(bucket.dwbpEnabled)
  }
  return null
}

/** @deprecated Use isAnySitePreferenceEnabled / readSiteLevelPreference for preferences[]. */
export const legacyIsDwbEnabledAtSite = site => {
  const fromSitePreferences = readDwbEnabledFromPreferenceBucket(
    site?.sitePreferences,
  )
  if (fromSitePreferences !== null) return fromSitePreferences

  if (site?.sitePreferences && typeof site.sitePreferences === 'object') {
    for (const programKey of Object.keys(site.sitePreferences)) {
      const fromProgram = readDwbEnabledFromPreferenceBucket(
        site.sitePreferences[programKey],
      )
      if (fromProgram !== null) return fromProgram
    }
  }

  if (site?.programs && typeof site.programs === 'object') {
    for (const programKey of Object.keys(site.programs)) {
      const fromProgram = readDwbEnabledFromPreferenceBucket(
        site.programs[programKey]?.preferences,
      )
      if (fromProgram !== null) return fromProgram
    }
  }

  return false
}

const getActiveLicenseProgramCodes = site => {
  if (!Array.isArray(site?.licenses)) return []
  const codes = new Set()
  site.licenses.forEach(({ isExpired, product }) => {
    if (isExpired || !Array.isArray(product?.programCodes)) return
    product.programCodes.forEach(code => codes.add(code))
  })
  return [...codes]
}

const legacySiteHasK8License = site =>
  site?.licenses?.some(({ isExpired, product }) => {
    return (
      product?.skuCode !== 'K8PF' &&
      !isExpired &&
      product?.programCodes?.some(code =>
        K8_LICENSE_PROGRAM_CODES.includes(code),
      )
    )
  })

const legacySiteHasHsLicense = site =>
  site?.licenses?.some(
    ({ isExpired, product }) =>
      !isExpired &&
      product?.programCodes?.some(code =>
        HS_LICENSE_PROGRAM_CODES.includes(code),
      ),
  )

/**
 * Matches legacy AdminDashboard buildAggregatePrograms: union programCodes from
 * every license on the site (including expired), without K8PF filtering.
 */
const legacyCollectProgramCodesFromSiteLicenses = site => {
  const codes = new Set()
  if (!Array.isArray(site?.licenses)) return codes

  site.licenses.forEach(license => {
    license?.product?.programCodes?.forEach(code => codes.add(code))
  })
  return codes
}

const legacySiteHasProgramCodeFromAggregateLicenses = (site, programCode) =>
  legacyCollectProgramCodesFromSiteLicenses(site).has(programCode)

/** @deprecated Use *_enabled site preferences when surfaced on sites/me. */
export const legacyIsProgramEnabledAtSite = (site, programKey) => {
  switch (programKey) {
    case 'K8':
      return legacySiteHasK8License(site)
    case 'HS':
      return legacySiteHasHsLicense(site)
    case 'BPU':
      return legacySiteHasProgramCodeFromAggregateLicenses(site, 'BPU')
    case 'CPU':
      return legacySiteHasProgramCodeFromAggregateLicenses(site, 'CPU')
    case 'SELA':
      return legacySiteHasProgramCodeFromAggregateLicenses(site, 'ADL')
    case 'DWB':
      return legacyIsDwbEnabledAtSite(site)
    default:
      return false
  }
}

/** @deprecated Use hsp_enabled / k5p_enabled / msp_enabled when surfaced on sites/me. */
export const legacyIsBlockedForStandaloneDwb = site => {
  const capabilityCodes = extractProgramCodesFromCapabilities(
    site?.capabilities,
  )
  if (
    capabilityCodes.some(code => STANDALONE_DWB_BLOCKED_PROGRAMS.includes(code))
  ) {
    return true
  }
  return getActiveLicenseProgramCodes(site).some(code =>
    STANDALONE_DWB_BLOCKED_PROGRAMS.includes(code),
  )
}
